import { victuallingPeriod } from '@felix/domain';
import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Group,
  Modal,
  NumberInput,
  Pagination,
  SegmentedControl,
  Select,
  Skeleton,
  Stack,
  TextInput,
  UnstyledButton,
} from '@mantine/core';
import {
  IconAlertCircle,
  IconCalendarStats,
  IconChartBar,
  IconCircleCheck,
  IconCoin,
  IconFileSpreadsheet,
  IconFileTypePdf,
  IconPigMoney,
  IconPlus,
  IconRefresh,
  IconReportMoney,
  IconScale,
  IconShip,
  IconTrash,
  IconUsers,
  IconUsersGroup,
} from '@tabler/icons-react';
import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EmptyState } from '../../EmptyState.js';
import { formatDay } from '../../format.js';
import { PageHeader } from '../../PageHeader.js';
import { Panel } from '../../Panel.js';
import { StatTile } from '../../StatTile.js';
import { followLink, useShell } from '../../shell-context.js';
import { formatMoney, formatQuantity, formatUnit } from '../materials/inventory.js';
import stock from '../materials/Stock.module.css';
import { monthLabel } from '../payroll/view.js';
import { pdfErrorKey, renderPdf } from '../personnel/pdf.js';
import { errorCode } from '../personnel/source.js';
import type { ProvisionSource } from './source.js';
import type {
  ProvisionSettings,
  VictuallingBudgetRate,
  VictuallingData,
  VictuallingFleetData,
} from './types.js';
import css from './Victualling.module.css';
import { victuallingFleetPdf, victuallingPdf } from './victualling-pdf.js';
import {
  amountIn,
  FLOW_COLUMNS,
  fleetCsv,
  moneyText,
  previousMonth,
  signedPercent,
  victuallingCsv,
  victuallingMonths,
} from './victualling-view.js';

/** Ofiste filo görünümünün seçici değeri. */
const FLEET = 'fleet';
const PAGE_SIZE = 10;

type State =
  | { kind: 'loading' }
  | { kind: 'error'; code: string }
  | { kind: 'report'; data: VictuallingData }
  | { kind: 'fleet'; data: VictuallingFleetData };

export interface VictuallingScreenProps {
  source: ProvisionSource;
  /** Açılış ayı (`YYYY-MM`); verilmezse son kapanan ay (bilgisayarın ayından önceki ay). */
  initialMonth?: string;
}

/**
 * Inventory ▸ Victualling Report (ADR 0123): aylık iaşe raporu, salt okuma. Tasarımı yok; dil
 * `gemi-envanter-kumanya.jpeg` (başlık kartı, beş özet kutusu, kartlarda tablolar). Gemide geminin
 * raporu; ofiste filo görünümü ("All vessels") ya da seçilen gemi. Tutarlar para birimi başına
 * (birimler toplanmaz); PDF ve CSV `provisions.export`; bütçe ofiste `settings.edit` ile.
 */
export function VictuallingScreen({ source, initialMonth }: VictuallingScreenProps) {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? 'en';
  const shell = useShell();
  const v = (key: string, options: Record<string, unknown> = {}) =>
    t(`victualling.${key}`, options);
  const office = source.scope === 'web';
  const [vesselId, setVesselId] = useState<string | null>(
    office && source.victuallingFleet ? FLEET : null,
  );
  // Aylık rapor kapanan ay için verilir: varsayılan son kapanan ay; içinde bulunulan ay seçilebilir
  const [month, setMonth] = useState<string>(
    initialMonth ?? previousMonth(new Date().toISOString().slice(0, 7)),
  );
  const [state, setState] = useState<State>({ kind: 'loading' });
  const [today, setToday] = useState<string | null>(null);
  const [vessels, setVessels] = useState<{ id: string; name: string }[]>([]);
  const [currency, setCurrency] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [notice, setNotice] = useState<{ tone: 'success' | 'danger'; text: string } | null>(null);
  const [budgetOpen, setBudgetOpen] = useState(false);
  const [version, setVersion] = useState(0);
  // Bilgisayarın ayı gemide henüz başlamamışsa (saat farkı) bir kez önceki aya iner
  const fellBack = useRef(initialMonth !== undefined);

  const load = useCallback(async () => {
    setState((current) => (current.kind === 'loading' ? current : { kind: 'loading' }));
    try {
      if (vesselId === FLEET && source.victuallingFleet) {
        const data = await source.victuallingFleet(month);
        setVessels(data.vessels);
        setToday(data.today);
        setState({ kind: 'fleet', data });
      } else if (source.victualling) {
        const data = await source.victualling(month, office ? vesselId : null);
        setVessels(data.vessels ?? []);
        setToday(data.today);
        setState({ kind: 'report', data });
      } else {
        setState({ kind: 'error', code: 'UNAVAILABLE' });
      }
    } catch (error) {
      const code = errorCode(error);
      if (code === 'FUTURE_DATE' && !fellBack.current) {
        fellBack.current = true;
        setMonth(previousMonth(month));
        return;
      }
      setState({ kind: 'error', code });
    }
  }, [source, vesselId, month, office]);
  useEffect(() => {
    void version;
    void load();
  }, [load, version]);

  const months = useMemo(
    () =>
      victuallingMonths(today ?? `${month}-01`).map((m) => ({
        value: m,
        label: monthLabel(m, locale),
      })),
    [today, month, locale],
  );
  const report = state.kind === 'report' ? state.data : null;
  const fleet = state.kind === 'fleet' ? state.data : null;
  const can = report?.can ?? fleet?.can ?? null;
  const n = (value: number) => formatQuantity(value, locale);
  const money = (amount: number | null | undefined, cur: string) =>
    amount === null || amount === undefined ? '—' : formatMoney(amount, cur, locale);

  const brand = async () => (source.brand ? await source.brand().catch(() => null) : null);
  const exportPdf = async () => {
    if (!source.pdf || (!report && !fleet)) return;
    const brandValue = await brand();
    const tt = (key: string, options?: Record<string, unknown>) => t(key, options ?? {});
    const printedAt = new Date().toISOString();
    const render = () =>
      renderPdf(
        report
          ? victuallingPdf(tt, { report, brand: brandValue, printedAt, language: locale })
          : victuallingFleetPdf(tt, {
              fleet: fleet as VictuallingFleetData,
              brand: brandValue,
              printedAt,
              language: locale,
            }),
      );
    const name = report
      ? `victualling-${slug(report.vessel.name)}-${report.month}.pdf`
      : `victualling-fleet-${month}.pdf`;
    try {
      await source.pdf.save(name, render);
      setNotice({ tone: 'success', text: v('notices.file', { name }) });
    } catch (error) {
      setNotice({ tone: 'danger', text: t(pdfErrorKey(errorCode(error))) });
    }
  };
  const exportCsv = async () => {
    if (!report && !fleet) return;
    const text = report ? victuallingCsv(report) : fleetCsv(fleet as VictuallingFleetData);
    const name = report
      ? `victualling-${slug(report.vessel.name)}-${report.month}.csv`
      : `victualling-fleet-${month}.csv`;
    const bytes = new TextEncoder().encode(`﻿${text}`);
    try {
      if (source.saveFile) await source.saveFile(name, bytes, 'text/csv');
      else {
        const url = URL.createObjectURL(new Blob([bytes], { type: 'text/csv' }));
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = name;
        anchor.click();
        setTimeout(() => URL.revokeObjectURL(url), 60_000);
      }
      setNotice({ tone: 'success', text: v('notices.file', { name }) });
    } catch (error) {
      setNotice({ tone: 'danger', text: t(pdfErrorKey(errorCode(error))) });
    }
  };

  const fact = (label: string, value: ReactNode, icon?: ReactNode) => (
    <div>
      <dt>{label}</dt>
      <dd>
        {icon ? (
          <span className={stock.factIcon} aria-hidden>
            {icon}
          </span>
        ) : null}
        <span>{value}</span>
      </dd>
    </div>
  );
  // Dönem: geminin raporundan; filoda en ileri geminin bugününe göre
  const period = report
    ? report
    : fleet
      ? (() => {
          const p = victuallingPeriod(fleet.month, fleet.today);
          return { periodStart: p.start, periodEnd: p.end, complete: p.complete };
        })()
      : null;
  const link = (path: string, label: string) => (
    <a href={path} onClick={(event) => shell && followLink(event, path, shell.navigate)}>
      {label}
    </a>
  );

  return (
    <div className={css.screen}>
      <PageHeader title={t('menu.inventoryVictualling')} titleHidden />
      <div className={css.top}>
        <section className={`${stock.head} ${css.head}`} aria-label={v('head')}>
          <div className={stock.headTitle}>
            <span className={stock.headIcon} aria-hidden>
              <IconReportMoney size={30} stroke={1.5} />
            </span>
            <div className={stock.headText}>
              <strong>{v('title')}</strong>
              <nav className={stock.headLinks} aria-label={v('sections')}>
                {link('/inventory/spare-parts', t('menu.inventorySpareParts'))}
                <span aria-hidden>|</span>
                {link('/inventory/provision', t('menu.inventoryProvision'))}
                <span aria-hidden>|</span>
                <a
                  href="/inventory/provision/victualling"
                  aria-current="page"
                  onClick={(event) => event.preventDefault()}
                >
                  {t('menu.inventoryVictualling')}
                </a>
              </nav>
            </div>
          </div>
          <dl className={`${stock.headFacts} ${css.facts}`}>
            {fact(
              v('vessel'),
              fleet ? v('fleet') : (report?.vessel.name ?? '—'),
              <IconShip size={16} stroke={1.6} />,
            )}
            {fact(v('month'), monthLabel(month, locale), <IconCalendarStats size={16} />)}
            {fact(
              v('period'),
              period ? `${formatDay(period.periodStart)} – ${formatDay(period.periodEnd)}` : '—',
            )}
            {fact(v('status'), period ? (period.complete ? v('complete') : v('toDate')) : '—')}
          </dl>
        </section>
        <div className={css.controls}>
          {office && (vessels.length > 0 || source.victuallingFleet) ? (
            <Select
              className={css.vesselSelect}
              label={v('vessel')}
              data={[
                ...(source.victuallingFleet ? [{ value: FLEET, label: v('fleet') }] : []),
                ...vessels.map((x) => ({ value: x.id, label: x.name })),
              ]}
              value={vesselId ?? report?.vessel.id ?? null}
              allowDeselect={false}
              onChange={(value) => {
                setVesselId(value);
                setPage(1);
              }}
            />
          ) : null}
          <Select
            className={css.monthSelect}
            label={v('month')}
            data={months}
            value={month}
            allowDeselect={false}
            onChange={(value) => {
              if (!value) return;
              fellBack.current = true;
              setMonth(value);
              setPage(1);
            }}
          />
          <div className={css.buttons}>
            {can?.export && source.pdf ? (
              <Button
                variant="default"
                leftSection={<IconFileTypePdf size={16} />}
                onClick={() => void exportPdf()}
              >
                {v('exportPdf')}
              </Button>
            ) : null}
            {can?.export ? (
              <Button
                variant="default"
                leftSection={<IconFileSpreadsheet size={16} />}
                onClick={() => void exportCsv()}
              >
                {v('exportCsv')}
              </Button>
            ) : null}
            {office && can?.settings && source.office ? (
              <Button
                variant="default"
                leftSection={<IconPigMoney size={16} />}
                onClick={() => setBudgetOpen(true)}
              >
                {v('budget')}
              </Button>
            ) : null}
          </div>
        </div>
      </div>

      {notice ? (
        <Alert
          color={notice.tone === 'success' ? 'green' : 'red'}
          icon={
            notice.tone === 'success' ? (
              <IconCircleCheck size={18} />
            ) : (
              <IconAlertCircle size={18} />
            )
          }
          withCloseButton
          closeButtonLabel={v('closeNotice')}
          onClose={() => setNotice(null)}
          role={notice.tone === 'success' ? 'status' : 'alert'}
        >
          {notice.text}
        </Alert>
      ) : null}

      {state.kind === 'error' ? (
        <Panel>
          <EmptyState
            tone="danger"
            title={v(`errors.${state.code}`, { defaultValue: v('errors.load') })}
            action={
              <Button
                size="xs"
                variant="light"
                leftSection={<IconRefresh size={14} />}
                onClick={() => setVersion((x) => x + 1)}
              >
                {v('retry')}
              </Button>
            }
          />
        </Panel>
      ) : null}

      {state.kind === 'loading' ? (
        <section className={css.tiles} aria-label={v('summary')}>
          {[0, 1, 2, 3, 4].map((i) => (
            <Skeleton key={i} height={72} radius="md" />
          ))}
        </section>
      ) : null}

      {report ? (
        <ReportView
          report={report}
          currency={currency}
          onCurrency={setCurrency}
          page={page}
          onPage={setPage}
          money={money}
          n={n}
        />
      ) : null}

      {fleet ? (
        <FleetView
          fleet={fleet}
          money={money}
          n={n}
          onOpen={(id) => {
            setVesselId(id);
            setPage(1);
          }}
        />
      ) : null}

      {budgetOpen && source.office ? (
        <BudgetDialog
          load={() => (source.office as NonNullable<ProvisionSource['office']>).settings()}
          save={(input) =>
            (source.office as NonNullable<ProvisionSource['office']>).saveSettings(input)
          }
          onClose={(saved) => {
            setBudgetOpen(false);
            if (saved) {
              setNotice({ tone: 'success', text: v('budgetDialog.saved') });
              setVersion((x) => x + 1);
            }
          }}
        />
      ) : null}
    </div>
  );
}

function slug(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

type Money = (amount: number | null | undefined, currency: string) => string;

export function ReportView({
  report,
  currency,
  onCurrency,
  page,
  onPage,
  money,
  n,
}: {
  report: VictuallingData;
  currency: string | null;
  onCurrency: (currency: string) => void;
  page: number;
  onPage: (page: number) => void;
  money: Money;
  n: (value: number) => string;
}) {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? 'en';
  const v = (key: string, options: Record<string, unknown> = {}) =>
    t(`victualling.${key}`, options);
  // Varsayılan para birimi: tüketimi en büyük olan (geminin hesap birimi), yoksa ilki
  const main =
    [...report.totals.consumption].sort((a, b) => b.amount - a.amount)[0]?.currency ??
    report.currencies[0] ??
    null;
  const selected = currency && report.currencies.includes(currency) ? currency : main;
  // Bütçe kutusu ana para biriminin (tüketimi en büyük) karşılaştırması; öteki birimler tabloda
  const budgeted =
    report.rates.find((r) => r.currency === main && r.budget !== null) ??
    report.rates.find((r) => r.budget !== null) ??
    null;
  const variance = budgeted?.budget?.variancePct ?? null;
  const rateText =
    report.rates.length === 0
      ? '—'
      : report.rates.map((r) => money(r.rate, r.currency)).join(' · ');
  const consumptionText =
    report.rates.length === 0
      ? '—'
      : report.rates.map((r) => money(r.consumption, r.currency)).join(' · ');
  const pages = Math.max(1, Math.ceil(report.items.length / PAGE_SIZE));
  const current = Math.min(page, pages);
  const visible = report.items.slice((current - 1) * PAGE_SIZE, current * PAGE_SIZE);
  const peak = report.dailyPob.reduce((max, d) => Math.max(max, d.pob), 0);
  const flowTitle = (key: (typeof FLOW_COLUMNS)[number]) => v(`categories.${key}`);
  const amountText = (list: { currency: string; amount: number }[]) =>
    selected ? money(amountIn(list, selected) ?? 0, selected) : '—';

  return (
    <>
      <section className={css.tiles} aria-label={v('summary')}>
        <StatTile
          variant="plain"
          icon={<IconUsersGroup size={22} />}
          label={v('cards.personDays')}
          value={n(report.personDays)}
        />
        <StatTile
          variant="plain"
          icon={<IconUsers size={22} />}
          label={v('cards.averagePob')}
          value={n(report.averagePob)}
        />
        <StatTile
          variant="plain"
          icon={<IconCoin size={22} />}
          label={v('cards.consumption')}
          value={consumptionText}
        />
        <StatTile
          variant="plain"
          icon={<IconChartBar size={22} />}
          label={v('cards.rate')}
          value={rateText}
          tone="success"
        />
        <StatTile
          variant="plain"
          icon={<IconScale size={22} />}
          label={v('cards.budget')}
          value={signedPercent(variance, locale)}
          tone={variance === null ? 'default' : variance > 0 ? 'danger' : 'success'}
        />
      </section>

      <div className={css.cards}>
        <Panel title={v('rates.title')} icon={<IconChartBar size={18} />}>
          {report.rates.length === 0 ? (
            <p className={css.muted}>{v('rates.none')}</p>
          ) : (
            <section
              className={css.tableBox}
              aria-label={v('rates.table')}
              // biome-ignore lint/a11y/noNoninteractiveTabindex: kaydırılabilir bölge (WCAG 2.1.1)
              tabIndex={0}
            >
              <table className={css.table}>
                <thead>
                  <tr>
                    <th scope="col">{v('rates.currency')}</th>
                    <th scope="col" data-numeric>
                      {v('rates.consumption')}
                    </th>
                    <th scope="col" data-numeric>
                      {v('rates.rate')}
                    </th>
                    <th scope="col" data-numeric>
                      {v('rates.budgetRate')}
                    </th>
                    <th scope="col" data-numeric>
                      {v('rates.variance')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {report.rates.map((r) => (
                    <tr key={r.currency}>
                      <td>{r.currency}</td>
                      <td data-numeric>{money(r.consumption, r.currency)}</td>
                      <td data-numeric>{money(r.rate, r.currency)}</td>
                      <td data-numeric>
                        {r.budget ? money(r.budget.dailyRate, r.currency) : v('rates.noBudget')}
                      </td>
                      <td data-numeric>
                        {r.budget ? (
                          <span className={r.budget.variance > 0 ? css.negative : css.positive}>
                            {signedPercent(r.budget.variancePct, locale)}
                            <span className={css.sub}>{money(r.budget.variance, r.currency)}</span>
                          </span>
                        ) : (
                          '—'
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          )}
          {report.personDays === 0 ? <p className={css.note}>{v('rates.noPersonDays')}</p> : null}
          <p className={css.note}>{v('rates.basis')}</p>
        </Panel>

        <Panel title={v('costCenters.title')} icon={<IconCoin size={18} />}>
          {report.costCenters.length === 0 ? (
            <p className={css.muted}>{v('costCenters.empty')}</p>
          ) : (
            <section
              className={css.tableBox}
              aria-label={v('costCenters.table')}
              // biome-ignore lint/a11y/noNoninteractiveTabindex: kaydırılabilir bölge (WCAG 2.1.1)
              tabIndex={0}
            >
              <table className={css.table}>
                <thead>
                  <tr>
                    <th scope="col">{v('costCenters.costCenter')}</th>
                    <th scope="col" data-numeric>
                      {v('costCenters.consumption')}
                    </th>
                    <th scope="col" data-numeric>
                      {v('costCenters.inRate')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {report.costCenters.map((c) => (
                    <tr key={c.costCenter ?? '-'}>
                      <td>
                        {c.costCenter
                          ? t(`provisions.costCenters.${c.costCenter}`, {
                              defaultValue: c.costCenter,
                            })
                          : v('costCenters.none')}
                      </td>
                      <td data-numeric>{moneyText(c.consumption, locale)}</td>
                      <td data-numeric>
                        {c.inRateBasis ? v('costCenters.yes') : v('costCenters.no')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          )}
        </Panel>

        <Panel title={v('pob.title')} icon={<IconUsers size={18} />}>
          <dl className={css.pobFacts}>
            <div>
              <dt>{v('pob.average')}</dt>
              <dd>{n(report.averagePob)}</dd>
            </div>
            <div>
              <dt>{v('pob.peak')}</dt>
              <dd>{n(peak)}</dd>
            </div>
            <div>
              <dt>{v('pob.days')}</dt>
              <dd>{n(report.days)}</dd>
            </div>
          </dl>
          <div className={css.bars} role="img" aria-label={v('pob.chart')}>
            {report.dailyPob.map((d) => (
              <span
                key={d.day}
                title={`${formatDay(d.day)}: ${d.pob}`}
                style={{ height: `${peak > 0 ? Math.max(4, (d.pob / peak) * 100) : 0}%` }}
              />
            ))}
          </div>
          <p className={css.note}>{v('pob.note')}</p>
        </Panel>
      </div>

      <Panel
        title={v('categories.title')}
        icon={<IconReportMoney size={18} />}
        extra={
          report.currencies.length > 1 && selected ? (
            <SegmentedControl
              size="xs"
              aria-label={v('categories.currency')}
              data={report.currencies}
              value={selected}
              onChange={onCurrency}
            />
          ) : null
        }
      >
        {report.categories.length === 0 ? (
          <p className={css.muted}>{v('categories.empty')}</p>
        ) : (
          <section
            className={css.tableBox}
            aria-label={v('categories.table')}
            // biome-ignore lint/a11y/noNoninteractiveTabindex: kaydırılabilir bölge (WCAG 2.1.1)
            tabIndex={0}
          >
            <table className={css.table}>
              <thead>
                <tr>
                  <th scope="col">{v('categories.category')}</th>
                  <th scope="col" data-numeric>
                    {v('categories.items')}
                  </th>
                  {FLOW_COLUMNS.map((key) => (
                    <th key={key} scope="col" data-numeric>
                      {selected ? `${flowTitle(key)} (${selected})` : flowTitle(key)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {report.categories.map((c) => (
                  <tr key={`${c.categoryCode ?? '-'}|${c.mainCategory}`}>
                    <td data-name>
                      {c.mainCategory}
                      <span className={css.sub}>
                        {c.categoryCode ?? v('categories.noCode')}
                        {c.unpricedItems > 0
                          ? ` · ${v('categories.unpriced', { count: c.unpricedItems })}`
                          : ''}
                      </span>
                    </td>
                    <td data-numeric>{n(c.items)}</td>
                    {FLOW_COLUMNS.map((key) => (
                      <td key={key} data-numeric>
                        {amountText(c.value[key])}
                      </td>
                    ))}
                  </tr>
                ))}
                <tr className={css.totalRow}>
                  <td>{v('categories.total')}</td>
                  <td data-numeric>{n(report.items.length)}</td>
                  {FLOW_COLUMNS.map((key) => (
                    <td key={key} data-numeric>
                      {amountText(report.totals[key])}
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </section>
        )}
        <p className={css.note}>{v('categories.equation')}</p>
      </Panel>

      <Panel title={v('items.title')} icon={<IconCalendarStats size={18} />}>
        {report.items.length === 0 ? (
          <p className={css.muted}>{v('items.empty')}</p>
        ) : (
          <>
            <section
              className={css.tableBox}
              aria-label={v('items.table')}
              // biome-ignore lint/a11y/noNoninteractiveTabindex: kaydırılabilir bölge (WCAG 2.1.1)
              tabIndex={0}
            >
              <table className={css.table}>
                <thead>
                  <tr>
                    <th scope="col">{v('items.item')}</th>
                    <th scope="col">{v('items.unit')}</th>
                    {FLOW_COLUMNS.map((key) => (
                      <th key={key} scope="col" data-numeric>
                        {flowTitle(key)}
                      </th>
                    ))}
                    <th scope="col" data-numeric>
                      {selected ? v('items.value', { currency: selected }) : v('items.valueAny')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {visible.map((item) => (
                    <tr key={item.stockItemId}>
                      <td data-name>
                        {item.name}
                        <span className={css.sub}>
                          {[item.itemCode, item.mainCategory].filter(Boolean).join(' · ')}
                        </span>
                        {item.unpriced || !item.inRateBasis ? (
                          <Group gap={4} mt={4}>
                            {item.unpriced ? (
                              <Badge size="xs" color="orange" variant="light">
                                {v('items.unpriced')}
                              </Badge>
                            ) : null}
                            {item.inRateBasis ? null : (
                              <Badge size="xs" color="gray" variant="light">
                                {v('items.notInRate')}
                              </Badge>
                            )}
                          </Group>
                        ) : null}
                      </td>
                      <td>{formatUnit(item.unit)}</td>
                      {FLOW_COLUMNS.map((key) => (
                        <td key={key} data-numeric>
                          {n(item.quantity[key])}
                        </td>
                      ))}
                      <td data-numeric>
                        {selected
                          ? money(amountIn(item.value.consumption, selected), selected)
                          : moneyText(item.value.consumption, locale)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
            <div className={css.pager}>
              <span>
                {v('items.showing', {
                  from: (current - 1) * PAGE_SIZE + 1,
                  to: Math.min(current * PAGE_SIZE, report.items.length),
                  total: report.items.length,
                })}
              </span>
              {pages > 1 ? (
                <nav aria-label={v('items.pagination')}>
                  <Pagination
                    size="sm"
                    total={pages}
                    value={current}
                    onChange={onPage}
                    getControlProps={(control) => ({ 'aria-label': v(`items.page.${control}`) })}
                    getItemProps={(page) => ({ 'aria-label': v('items.page.number', { page }) })}
                  />
                </nav>
              ) : null}
            </div>
          </>
        )}
      </Panel>
    </>
  );
}

export function FleetView({
  fleet,
  money,
  n,
  onOpen,
}: {
  fleet: VictuallingFleetData;
  money: Money;
  n: (value: number) => string;
  onOpen: (vesselId: string) => void;
}) {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? 'en';
  const v = (key: string, options: Record<string, unknown> = {}) =>
    t(`victualling.${key}`, options);
  const personDays = fleet.rows.reduce((sum, r) => sum + r.personDays, 0);
  const over = fleet.rows.filter((r) =>
    r.rates.some((rate) => rate.budget !== null && rate.budget.variance > 0),
  ).length;
  const unpriced = fleet.rows.reduce((sum, r) => sum + r.unpricedItems, 0);
  const fleetRate =
    fleet.totals.length === 0
      ? '—'
      : fleet.totals.map((x) => money(x.rate, x.currency)).join(' · ');
  return (
    <>
      <section className={css.tiles} aria-label={v('summary')}>
        <StatTile
          variant="plain"
          icon={<IconShip size={22} />}
          label={v('cards.vessels')}
          value={n(fleet.rows.length)}
        />
        <StatTile
          variant="plain"
          icon={<IconUsersGroup size={22} />}
          label={v('cards.personDays')}
          value={n(personDays)}
        />
        <StatTile
          variant="plain"
          icon={<IconChartBar size={22} />}
          label={v('cards.fleetRate')}
          value={fleetRate}
          tone="success"
        />
        <StatTile
          variant="plain"
          icon={<IconScale size={22} />}
          label={v('cards.overBudget')}
          value={n(over)}
          tone={over > 0 ? 'danger' : 'default'}
        />
        <StatTile
          variant="plain"
          icon={<IconAlertCircle size={22} />}
          label={v('cards.unpriced')}
          value={n(unpriced)}
          tone={unpriced > 0 ? 'warning' : 'default'}
        />
      </section>
      <Panel title={v('fleetView.title')} icon={<IconReportMoney size={18} />}>
        {fleet.rows.length === 0 ? (
          <p className={css.muted}>{v('fleetView.empty')}</p>
        ) : (
          <section
            className={css.tableBox}
            aria-label={v('fleetView.table')}
            // biome-ignore lint/a11y/noNoninteractiveTabindex: kaydırılabilir bölge (WCAG 2.1.1)
            tabIndex={0}
          >
            <table className={css.table}>
              <thead>
                <tr>
                  <th scope="col">{v('fleetView.vessel')}</th>
                  <th scope="col" data-numeric>
                    {v('cards.personDays')}
                  </th>
                  <th scope="col" data-numeric>
                    {v('cards.averagePob')}
                  </th>
                  <th scope="col">{v('rates.currency')}</th>
                  <th scope="col" data-numeric>
                    {v('rates.consumption')}
                  </th>
                  <th scope="col" data-numeric>
                    {v('rates.rate')}
                  </th>
                  <th scope="col" data-numeric>
                    {v('rates.budgetRate')}
                  </th>
                  <th scope="col" data-numeric>
                    {v('rates.variance')}
                  </th>
                  <th scope="col" data-numeric>
                    {v('cards.unpriced')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {fleet.rows.flatMap((row) => {
                  const rates = row.rates.length > 0 ? row.rates : [null];
                  return rates.map((rate, i) => (
                    <tr key={`${row.vesselId}|${rate?.currency ?? '-'}`}>
                      {i === 0 ? (
                        <>
                          <td rowSpan={rates.length} data-name>
                            <UnstyledButton
                              className={stock.linkButton}
                              aria-label={v('fleetView.open', { name: row.name })}
                              onClick={() => onOpen(row.vesselId)}
                            >
                              {row.name}
                            </UnstyledButton>
                          </td>
                          <td rowSpan={rates.length} data-numeric>
                            {n(row.personDays)}
                          </td>
                          <td rowSpan={rates.length} data-numeric>
                            {n(row.averagePob)}
                          </td>
                        </>
                      ) : null}
                      <td>
                        {rate?.currency ?? (
                          <span className={css.sub}>{v('fleetView.noConsumption')}</span>
                        )}
                      </td>
                      <td data-numeric>{rate ? money(rate.consumption, rate.currency) : '—'}</td>
                      <td data-numeric>{rate ? money(rate.rate, rate.currency) : '—'}</td>
                      <td data-numeric>
                        {rate?.budget
                          ? money(rate.budget.dailyRate, rate.currency)
                          : rate
                            ? v('rates.noBudget')
                            : '—'}
                      </td>
                      <td data-numeric>
                        {rate?.budget ? (
                          <span className={rate.budget.variance > 0 ? css.negative : css.positive}>
                            {signedPercent(rate.budget.variancePct, locale)}
                          </span>
                        ) : (
                          '—'
                        )}
                      </td>
                      {i === 0 ? (
                        <td rowSpan={rates.length} data-numeric>
                          {n(row.unpricedItems)}
                        </td>
                      ) : null}
                    </tr>
                  ));
                })}
                {fleet.totals.map((total) => (
                  <tr key={`total|${total.currency}`} className={css.totalRow}>
                    <td>{v('fleetView.total', { count: total.vessels })}</td>
                    <td data-numeric>{n(total.personDays)}</td>
                    <td />
                    <td>{total.currency}</td>
                    <td data-numeric>{money(total.consumption, total.currency)}</td>
                    <td data-numeric>{money(total.rate, total.currency)}</td>
                    <td />
                    <td />
                    <td />
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        )}
        <p className={css.note}>{v('rates.basis')}</p>
      </Panel>
    </>
  );
}

/** Ofiste şirketin iaşe bütçesi (ADR 0123 §5): para birimi başına günlük kişi başı oran. */
function BudgetDialog({
  load,
  save,
  onClose,
}: {
  load: () => Promise<ProvisionSettings>;
  save: (input: ProvisionSettings) => Promise<ProvisionSettings>;
  onClose: (saved: boolean) => void;
}) {
  const { t } = useTranslation();
  const v = (key: string, options: Record<string, unknown> = {}) =>
    t(`victualling.budgetDialog.${key}`, options);
  const [settings, setSettings] = useState<ProvisionSettings | null>(null);
  const [rows, setRows] = useState<{ key: number; currency: string; dailyRate: number | '' }[]>([]);
  const nextKey = useRef(0);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    load()
      .then((loaded) => {
        setSettings(loaded);
        setRows(
          (loaded.victuallingBudgets ?? []).map((b) => ({
            key: nextKey.current++,
            currency: b.currency,
            dailyRate: b.dailyRate,
          })),
        );
      })
      .catch((reason) => setError(errorCode(reason)));
  }, [load]);

  const cleaned = rows.map((r) => ({
    currency: r.currency.trim().toUpperCase(),
    dailyRate: r.dailyRate,
  }));
  const invalid = cleaned.some(
    (r) => !/^[A-Z]{3}$/.test(r.currency) || typeof r.dailyRate !== 'number' || r.dailyRate <= 0,
  );
  const duplicate = new Set(cleaned.map((r) => r.currency)).size !== cleaned.length;
  const submit = async () => {
    if (!settings || invalid || duplicate) return;
    setBusy(true);
    setError(null);
    try {
      await save({
        rowVersion: settings.rowVersion,
        expiryWarningDays: settings.expiryWarningDays,
        victuallingBudgets: cleaned as VictuallingBudgetRate[],
      });
      onClose(true);
    } catch (reason) {
      setError(errorCode(reason));
      setBusy(false);
    }
  };

  return (
    <Modal opened onClose={() => onClose(false)} title={v('title')} centered>
      <form
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
      >
        <Stack gap="sm">
          <p className={css.muted}>{v('hint')}</p>
          {error ? (
            <Alert color="red" icon={<IconAlertCircle size={18} />} role="alert">
              {t(`provisions.errors.${error}`, { defaultValue: t('victualling.errors.load') })}
            </Alert>
          ) : null}
          {!settings && !error ? <Skeleton height={60} /> : null}
          {settings && rows.length === 0 ? <p className={css.muted}>{v('empty')}</p> : null}
          {rows.map((row, index) => (
            <div key={row.key} className={css.budgetRow}>
              <TextInput
                label={v('currency')}
                maxLength={3}
                value={row.currency}
                onChange={(event) => {
                  const value = event.currentTarget.value;
                  setRows((list) =>
                    list.map((r, i) => (i === index ? { ...r, currency: value } : r)),
                  );
                }}
              />
              <NumberInput
                label={v('dailyRate')}
                min={0}
                decimalScale={2}
                value={row.dailyRate}
                onChange={(value) =>
                  setRows((list) =>
                    list.map((r, i) =>
                      i === index ? { ...r, dailyRate: typeof value === 'number' ? value : '' } : r,
                    ),
                  )
                }
              />
              <ActionIcon
                variant="subtle"
                color="red"
                size="lg"
                aria-label={v('remove', { currency: row.currency || '—' })}
                onClick={() => setRows((list) => list.filter((_, i) => i !== index))}
              >
                <IconTrash size={16} />
              </ActionIcon>
            </div>
          ))}
          {duplicate ? <p className={css.negative}>{v('duplicate')}</p> : null}
          {invalid && rows.length > 0 ? <p className={css.muted}>{v('invalid')}</p> : null}
          <Group justify="space-between">
            <Button
              variant="subtle"
              leftSection={<IconPlus size={16} />}
              disabled={!settings || rows.length >= 10}
              onClick={() =>
                setRows((list) => [
                  ...list,
                  { key: nextKey.current++, currency: '', dailyRate: '' },
                ])
              }
            >
              {v('add')}
            </Button>
            <Group gap="xs">
              <Button variant="default" onClick={() => onClose(false)}>
                {v('cancel')}
              </Button>
              <Button type="submit" loading={busy} disabled={!settings || invalid || duplicate}>
                {v('save')}
              </Button>
            </Group>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
